import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, isValid, parse } from "date-fns";
import { useLayoutEffect } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../navigators/HomeTabsNavigator/types";
import { DiningAgendaScreen } from "./AgendaScreens/DiningAgendaScreen";
import { DurationBasedAgendaScreen } from "./AgendaScreens/DurationBasedAgendaScreen";
import { MultiSlotBookingScreen } from "./AgendaScreens/MultiSlotBookingScreen";
import { TeeSheetScreen } from "./AgendaScreens/TeeSheetScreen";
import { AgendaFromFacilityTypePatronQuery } from "./__generated__/AgendaFromFacilityTypePatronQuery.graphql";

export const AgendaFromFacilityTypePatron = ({
  sessionRef,
}: {
  sessionRef: PreloadedQuery<AgendaFromFacilityTypePatronQuery>;
}) => {
  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const facilitySlug = route.params?.facilitySlug ?? "";
  const date = route.params?.date ?? "";
  /**
   * We check if our date matches our date format. If not, we are going to shunt
   * the user over to "today"
   */
  const isDateValid = isValid(parse(date, "yyyy-MM-dd", new Date()));

  const data = usePreloadedQuery(AgendaFromFacilityTypeQueryString, sessionRef);
  const club = data.session?.currentClientProfile?.member?.club;

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList, "Agenda">>();

  const navigationHome =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  useLayoutEffect(() => {
    if (!facilitySlug) {
      const slug =
        club?.__typename === "Club"
          ? club?.departments?.edges?.[0]?.node?.facilities?.edges?.[0]?.node
              ?.slug ?? ""
          : "";
      if (slug) {
        navigation.setParams({
          facilitySlug: slug,
        });
      } else {
        setTimeout(() => {
          navigationHome.navigate("MainHome");
        }, 200);
      }
    }
  }, [
    facilitySlug,
    navigation,
    navigationHome,
    club?.__typename,
    club?.departments?.edges,
  ]);

  useLayoutEffect(() => {
    if (!isDateValid || !date || date === "today") {
      const today = format(new Date(), "yyyy-MM-dd");
      navigation.setParams({
        date: today,
      });
    }
  }, [date, isDateValid, navigation]);

  const facilityType =
    club?.__typename === "Club"
      ? club.departments?.edges
          ?.find(
            (edge) =>
              edge?.node?.facilities?.edges?.findIndex(
                (facility) => facility?.node?.slug === facilitySlug
              ) !== -1
          )
          ?.node?.facilities?.edges?.find(
            (facility) => facility?.node?.slug === facilitySlug
          )?.node?.type
      : null;

  return facilitySlug === "dining" ? (
    <DiningAgendaScreen />
  ) : facilityType === "GOLF_COURSE" ? (
    <TeeSheetScreen />
  ) : club?.supportsPublic ? (
    <DurationBasedAgendaScreen />
  ) : (
    <MultiSlotBookingScreen />
  );
};

export const AgendaFromFacilityTypeQueryString = graphql`
  query AgendaFromFacilityTypePatronQuery {
    session {
      currentClientProfile {
        member {
          club {
            supportsPublic
            __typename
            departments {
              edges {
                node {
                  slug
                  facilities {
                    edges {
                      node {
                        slug
                        type
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
